import { api, CustomAxiosRequestConfig } from "@/shared/api/client";
import {
    ProjectGetDTO,
    ProjectsGetDTO,
    ProjectCreateDTO,
    ProjectUpdateDTO,
    ProjectStatusDTO,
    PageProjectsGetDTO,
    ProjectsRequestDTO,
    WalletProjectFileGetDTO,
    WalletProjectCreateDTO,
    ProjectMediaDTO,
    ProjectSection,
    ProjectGeneralDTO,
    ProjectScheduleDTO,
    ProjectTechnicalDTO,
    ProjectFinancialDTO,
    ProjectClientDTO,
} from "@/shared/types/project";
import { WalletDTO, WalletUploadDTO } from "@/shared/types/project";

export const projectService = {
    getAll: async (params: ProjectsRequestDTO): Promise<PageProjectsGetDTO> => {
        const queryParams: Record<string, unknown> = {
            page: params.page - 1,
            size: params.size,
            ...(params.filter || {})
        };

        if (params.sort && params.sort.length > 0) {
            queryParams.sort = params.sort.join(",");
        }

        const response = await api.get<PageProjectsGetDTO>("/projects", {
            params: queryParams,
        });
        return response.data;
    },

    getById: async (projectId: string): Promise<ProjectGetDTO> => {
        const response = await api.get<ProjectGetDTO>(`/projects/${projectId}`);
        return response.data;
    },

    /** Seções da obra que o usuário pode ver (ver ProjectSection). */
    getSections: async (projectId: string): Promise<ProjectSection[]> => {
        const response = await api.get<ProjectSection[]>(`/projects/${projectId}/sections`);
        return response.data;
    },

    getGeneral: async (projectId: string): Promise<ProjectGeneralDTO> => {
        const response = await api.get<ProjectGeneralDTO>(`/projects/${projectId}/general`);
        return response.data;
    },

    getSchedule: async (projectId: string): Promise<ProjectScheduleDTO> => {
        const response = await api.get<ProjectScheduleDTO>(`/projects/${projectId}/schedule`);
        return response.data;
    },

    getTechnical: async (projectId: string): Promise<ProjectTechnicalDTO> => {
        const response = await api.get<ProjectTechnicalDTO>(`/projects/${projectId}/technical`);
        return response.data;
    },

    getFinancial: async (projectId: string): Promise<ProjectFinancialDTO> => {
        const response = await api.get<ProjectFinancialDTO>(`/projects/${projectId}/financial`);
        return response.data;
    },

    getClient: async (projectId: string): Promise<ProjectClientDTO> => {
        const response = await api.get<ProjectClientDTO>(`/projects/${projectId}/client`);
        return response.data;
    },

    create: async (data: ProjectCreateDTO): Promise<ProjectGetDTO> => {
        const response = await api.post<ProjectGetDTO>("/projects", data);
        return response.data;
    },

    update: async (
        projectId: string,
        data: ProjectUpdateDTO
    ): Promise<ProjectGetDTO> => {
        const response = await api.put<ProjectGetDTO>(
            `/projects/${projectId}`,
            data
        );
        return response.data;
    },

    updateStatus: async (
        projectId: string,
        data: ProjectStatusDTO
    ): Promise<ProjectGetDTO> => {
        const response = await api.put<ProjectGetDTO>(
            `/projects/${projectId}/status`,
            data
        );
        return response.data;
    },

    start: async (projectId: string, startDate?: string): Promise<ProjectGetDTO> => {
        const response = await api.put<ProjectGetDTO>(
            `/projects/${projectId}/start`,
            startDate ? { startDate } : undefined
        );
        return response.data;
    },

    finish: async (projectId: string): Promise<void> => {
        await api.put(`/projects/${projectId}/finish`);
    },

    disable: async (projectId: string): Promise<void> => {
        await api.put(`/projects/${projectId}/disable`);
    },

    getWallet: async (projectId: string): Promise<WalletProjectFileGetDTO> => {
        const response = await api.get<WalletProjectFileGetDTO>(
            `/projects/${projectId}/wallets`,
            { skipErrorToast: true } as CustomAxiosRequestConfig
        );
        return response.data;
    },

    createWallet: async (
        projectId: string,
        data: WalletProjectCreateDTO
    ): Promise<WalletDTO> => {
        const response = await api.post<WalletDTO>(
            `/projects/${projectId}/wallets`,
            data,
            { skipErrorToast: true } as CustomAxiosRequestConfig
        );
        return response.data;
    },

    deleteWallet: async (projectId: string): Promise<void> => {
        await api.delete(`/projects/${projectId}/wallets`);
    },

    uploadWallet: async (
        projectId: string,
        data: WalletUploadDTO
    ): Promise<WalletProjectFileGetDTO> => {
        const response = await api.post<WalletProjectFileGetDTO>(
            `/projects/${projectId}/wallets/upload`,
            data
        );
        return response.data;
    },

    generateStepsReport: async (projectId: string): Promise<WalletProjectFileGetDTO> => {
        const response = await api.post<WalletProjectFileGetDTO>(`/projects/${projectId}/wallets/generate-steps-report`);
        return response.data;
    },

    generateReceivementsReport: async (projectId: string): Promise<WalletProjectFileGetDTO> => {
        const response = await api.post<WalletProjectFileGetDTO>(`/projects/${projectId}/wallets/generate-receivements-report`);
        return response.data;
    },

    generateProjectReport: async (projectId: string): Promise<WalletProjectFileGetDTO> => {
        const response = await api.post<WalletProjectFileGetDTO>(`/projects/${projectId}/wallets/generate-project-report`);
        return response.data;
    },

    generateProjectDiaryWorkerDiariesReport: async (projectId: string, projectDiaryId: string): Promise<WalletProjectFileGetDTO> => {
        const response = await api.post<WalletProjectFileGetDTO>(`/projects/${projectId}/wallets/generate-project-diary-worker-diaries-report/${projectDiaryId}`);
        return response.data;
    },

    generateProjectDiaryReport: async (projectId: string, projectDiaryId: string): Promise<WalletProjectFileGetDTO> => {
        const response = await api.post<WalletProjectFileGetDTO>(`/projects/${projectId}/wallets/generate-project-diary-report/${projectDiaryId}`);
        return response.data;
    },

    generateCostsReport: async (projectId: string): Promise<WalletProjectFileGetDTO> => {
        const response = await api.post<WalletProjectFileGetDTO>(`/projects/${projectId}/wallets/generate-costs-report`);
        return response.data;
    },

    addFile: async (
        projectId: string,
        file: File
    ): Promise<WalletProjectFileGetDTO> => {
        const formData = new FormData();
        formData.append("file", file);
        const response = await api.post<WalletProjectFileGetDTO>(
            `/projects/${projectId}/wallets/add-file`,
            formData,
            { skipErrorToast: true } as CustomAxiosRequestConfig
        );
        return response.data;
    },

    addCredential: async (
        projectId: string,
        credentials: Record<string, string>
    ): Promise<WalletDTO> => {
        const response = await api.post<WalletDTO>(
            `/projects/${projectId}/wallets/add-credential`,
            credentials,
            { skipErrorToast: true } as CustomAxiosRequestConfig
        );
        return response.data;
    },

    deleteFile: async (projectId: string, fileId: string): Promise<void> => {
        await api.delete(`/projects/${projectId}/wallets/delete-file/${fileId}`);
    },

    deleteCredential: async (
        projectId: string,
        credentialKey: string
    ): Promise<void> => {
        await api.delete(
            `/projects/${projectId}/wallets/delete-credential/${credentialKey}`
        );
    },

    uploadMedia: async (
        projectId: string,
        files: File[]
    ): Promise<ProjectMediaDTO[]> => {
        const formData = new FormData();
        files.forEach((file) => formData.append("files", file));

        const response = await api.post<ProjectMediaDTO[]>(
            `/projects/${projectId}/media`,
            formData
        );
        return response.data;
    },

    getAllMedia: async (projectId: string): Promise<ProjectMediaDTO[]> => {
        const response = await api.get<ProjectMediaDTO[]>(`/projects/${projectId}/media`);
        return response.data;
    },

    getMediaById: async (projectId: string, mediaId: string): Promise<Blob> => {
        const response = await api.get(
            `/projects/${projectId}/media/${mediaId}`,
            { responseType: 'blob' }
        );
        return response.data;
    },

    deleteMedia: async (projectId: string, mediaId: string): Promise<void> => {
        await api.delete(`/projects/${projectId}/media/${mediaId}`);
    },

    getProjectsPendingDiary: async (
        date: string,
        params: ProjectsRequestDTO
    ): Promise<PageProjectsGetDTO> => {
        const queryParams: Record<string, unknown> = {
            date,
            page: params.page - 1,
            size: params.size,
        };

        if (params.sort && params.sort.length > 0) {
            queryParams.sort = params.sort.join(",");
        }

        const response = await api.get<PageProjectsGetDTO>("/projects/pending-diary", {
            params: queryParams,
        });
        return response.data;
    },
};
